import type {
  QuestionDefinitionDTOType,
  QuestionnaireConditionDTOType,
  QuestionnaireConditionInnerGroupDTOType,
  QuestionnaireConditionLeafDTOType,
  QuestionnaireConditionMiddleGroupDTOType,
  QuestionnaireConditionOuterGroupDTOType,
  QuestionnaireDTOType,
  QuestionDefinition,
  QuestionnaireAnswerValue,
  QuestionnaireAnswers,
  QuestionnaireContext,
  QuestionnaireValidationState,
} from './types'

export const EMPTY_QUESTIONNAIRE_VALIDATION: QuestionnaireValidationState = {
  isValid: true,
  errors: {},
  visibleQuestionIds: [],
  requiredQuestionIds: [],
}

type QuestionDefaultObject = {
  if?: QuestionnaireConditionDTOType[]
  then?: QuestionnaireAnswerValue
  else?: QuestionnaireAnswerValue
}

type ValidationRules = {
  gt?: number
  gte?: number
  lt?: number
  lte?: number
  min?: number
  max?: number
  minimum?: number
  maximum?: number
  minLength?: number
  maxLength?: number
  min_length?: number
  max_length?: number
  pattern?: string
  regex?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isDefaultObject(value: unknown): value is QuestionDefaultObject {
  return isRecord(value) && ('if' in value || 'then' in value || 'else' in value)
}

function getComparableValues(value: QuestionnaireAnswerValue | undefined): unknown[] {
  if (value === undefined) return [null]
  if (Array.isArray(value)) return value
  return [value]
}

function primitiveValueToText(value: unknown): string | null {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return null
}

function answerValuePartToText(value: unknown): string {
  const primitiveText = primitiveValueToText(value)
  if (primitiveText !== null) return primitiveText
  if (value == null) return ''

  try {
    return JSON.stringify(value) ?? ''
  } catch {
    return ''
  }
}

function answerValueToText(value: QuestionnaireAnswerValue): string {
  if (Array.isArray(value)) return value.map(answerValuePartToText).join(',')
  return answerValuePartToText(value)
}

function valuesEqual(
  left: QuestionnaireAnswerValue | undefined,
  right: QuestionnaireAnswerValue,
): boolean {
  const leftValues = getComparableValues(left)
  const rightValues = getComparableValues(right)

  return leftValues.some((leftValue) =>
    rightValues.some((rightValue) => {
      if (leftValue === rightValue) return true
      if (leftValue == null || rightValue == null) return leftValue == null && rightValue == null
      const leftText = primitiveValueToText(leftValue)?.toLowerCase()
      const rightText = primitiveValueToText(rightValue)?.toLowerCase()
      return leftText !== undefined && rightText !== undefined && leftText === rightText
    }),
  )
}

type ConditionValueMatcher = QuestionnaireConditionLeafDTOType['valueMatcher']

type QuestionnaireConditionGroup =
  | QuestionnaireConditionOuterGroupDTOType
  | QuestionnaireConditionMiddleGroupDTOType
  | QuestionnaireConditionInnerGroupDTOType

function isQuestionnaireConditionGroup(
  condition: QuestionnaireConditionDTOType,
): condition is QuestionnaireConditionGroup {
  return 'operator' in condition && 'conditions' in condition
}

function conditionValue(
  condition: QuestionnaireConditionLeafDTOType,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext,
): QuestionnaireAnswerValue | undefined {
  const questionId = condition.questionId ?? undefined
  const configId = condition.configId ?? undefined

  if (questionId) return answers[questionId]
  if (configId) return context[configId]
  return undefined
}

export function conditionMatchesValue(
  value: QuestionnaireAnswerValue | undefined,
  matcher: ConditionValueMatcher | undefined,
): boolean {
  if (!matcher) return Boolean(value)

  if ('equals' in matcher && matcher.equals !== undefined) {
    return valuesEqual(value, matcher.equals)
  }

  if ('anyOf' in matcher && Array.isArray(matcher.anyOf)) {
    return matcher.anyOf.some((candidate) => valuesEqual(value, candidate))
  }

  return true
}

export function evaluateQuestionnaireCondition(
  condition: QuestionnaireConditionDTOType,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): boolean {
  if (isQuestionnaireConditionGroup(condition)) {
    if (condition.operator === 'or') {
      return condition.conditions.some((nestedCondition) =>
        evaluateQuestionnaireCondition(nestedCondition, answers, context),
      )
    }
    return condition.conditions.every((nestedCondition) =>
      evaluateQuestionnaireCondition(nestedCondition, answers, context),
    )
  }

  return conditionMatchesValue(conditionValue(condition, answers, context), condition.valueMatcher)
}

export function evaluateQuestionnaireConditions(
  conditions: QuestionnaireConditionDTOType[] | undefined,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): boolean {
  if (!conditions?.length) return true
  return conditions.every((condition) =>
    evaluateQuestionnaireCondition(condition, answers, context),
  )
}

export function isQuestionVisible(
  question: QuestionDefinitionDTOType,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): boolean {
  return evaluateQuestionnaireConditions(question.displayIf, answers, context)
}

export function isQuestionRequired(
  question: QuestionDefinitionDTOType,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): boolean {
  if (question.required) return true
  if (!question.requiredIf?.length) return false
  return evaluateQuestionnaireConditions(question.requiredIf, answers, context)
}

export function isAnswerEmpty(value: QuestionnaireAnswerValue | undefined): boolean {
  if (value == null) return true
  if (Array.isArray(value)) {
    if (value.length === 0) return true
    return value.every((item) => item == null || (typeof item === 'string' && item.trim() === ''))
  }
  if (typeof value === 'string') return value.trim() === ''
  return false
}

function getQuestionDefaultValue(
  question: QuestionDefinitionDTOType,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext,
): QuestionnaireAnswerValue | undefined {
  if (!('default' in question)) return undefined
  const defaultValue = question.default

  if (isDefaultObject(defaultValue)) {
    return evaluateQuestionnaireConditions(defaultValue.if, answers, context)
      ? (defaultValue.then ?? null)
      : (defaultValue.else ?? null)
  }

  return defaultValue
}

export function getInitialQuestionnaireAnswers(
  questionnaire: QuestionnaireDTOType | null | undefined,
  context: QuestionnaireContext = {},
  existingAnswers: QuestionnaireAnswers = {},
): QuestionnaireAnswers {
  if (!questionnaire) return existingAnswers

  return questionnaire.pages.reduce<QuestionnaireAnswers>(
    (answers, page) => {
      if (!evaluateQuestionnaireConditions(page.displayIf, answers, context)) return answers

      page.questions.forEach((question) => {
        if (Object.hasOwn(answers, question.questionId)) return
        const defaultValue = getQuestionDefaultValue(question, answers, context)
        if (defaultValue !== undefined) {
          answers[question.questionId] = defaultValue
        }
      })

      return answers
    },
    { ...existingAnswers },
  )
}

export function getVisibleQuestionIds(
  questionnaire: QuestionnaireDTOType | null | undefined,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): string[] {
  if (!questionnaire) return []

  return questionnaire.pages.flatMap((page) => {
    if (!evaluateQuestionnaireConditions(page.displayIf, answers, context)) return []
    return page.questions
      .filter((question) => isQuestionVisible(question, answers, context))
      .map((question) => question.questionId)
  })
}

export type VisibleQuestionnairePage = QuestionnaireDTOType['pages'][number]

export type VisibleQuestionEntry = {
  page: VisibleQuestionnairePage
  question: QuestionDefinitionDTOType
}

export function getVisiblePages(
  questionnaire: QuestionnaireDTOType,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): VisibleQuestionnairePage[] {
  return questionnaire.pages
    .filter((page) => evaluateQuestionnaireConditions(page.displayIf, answers, context))
    .map((page) => ({
      ...page,
      questions: page.questions.filter((question) => isQuestionVisible(question, answers, context)),
    }))
    .filter((page) => page.questions.length > 0)
}

export function getVisibleQuestionEntries(
  questionnaire: QuestionnaireDTOType,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): VisibleQuestionEntry[] {
  return getVisiblePages(questionnaire, answers, context).flatMap((page) =>
    page.questions.map((question) => ({ page, question })),
  )
}

export function isErrorContentQuestion(question: QuestionDefinition): boolean {
  return question.type === 'content' && question.variant === 'error'
}

export function isConfirmEligibleContentQuestion(question: QuestionDefinition): boolean {
  return question.type === 'content' && question.variant === 'confirm_eligible'
}

/** True when no visible page blocks continuing (`allowContinue === false`). */
export function isEligibleForKitRegistration(
  questionnaire: QuestionnaireDTOType,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): boolean {
  return !getVisiblePages(questionnaire, answers, context).some(
    (page) => page.allowContinue === false,
  )
}

type QuestionnaireConditionNode =
  | QuestionnaireConditionDTOType
  | QuestionnaireConditionMiddleGroupDTOType
  | QuestionnaireConditionInnerGroupDTOType

function conditionReferencesQuestion(
  condition: QuestionnaireConditionNode,
  questionId: string,
): boolean {
  if ('conditions' in condition) {
    return condition.conditions.some((nestedCondition) =>
      conditionReferencesQuestion(nestedCondition, questionId),
    )
  }

  return condition.questionId === questionId
}

function visibleStopPageBlocksAnswer(page: VisibleQuestionnairePage, questionId: string): boolean {
  if (page.allowContinue !== false) return false

  const conditions = page.displayIf
  if (!conditions?.length) return true

  return conditions.some((condition) => conditionReferencesQuestion(condition, questionId))
}

/**
 * True when this answer does not open a visible stop page.
 * Another answer can still block registration without changing this flag.
 */
export function isResponseEligibleForKitRegistration(
  questionnaire: QuestionnaireDTOType,
  answers: QuestionnaireAnswers,
  questionId: string,
  context: QuestionnaireContext = {},
): boolean {
  return !getVisiblePages(questionnaire, answers, context).some((page) =>
    visibleStopPageBlocksAnswer(page, questionId),
  )
}

function validationRulesFor(question: QuestionDefinitionDTOType): ValidationRules {
  return isRecord(question.validationRules) ? question.validationRules : {}
}

function validateRules(
  question: QuestionDefinitionDTOType,
  value: QuestionnaireAnswerValue,
): string | null {
  const rules = validationRulesFor(question)
  const valueText = answerValueToText(value)

  const minLength = rules.minLength ?? rules.min_length
  if (typeof minLength === 'number' && valueText.length < minLength) {
    return `${question.label} must be at least ${minLength} characters`
  }

  const maxLength = rules.maxLength ?? rules.max_length
  if (typeof maxLength === 'number' && valueText.length > maxLength) {
    return `${question.label} must be ${maxLength} characters or fewer`
  }

  if (question.type === 'number') {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return `${question.label} must be a number`

    if (typeof rules.gt === 'number' && numericValue <= rules.gt) {
      return `${question.label} must be greater than ${rules.gt}`
    }

    const inclusiveMin = rules.gte ?? rules.min ?? rules.minimum
    if (typeof inclusiveMin === 'number' && numericValue < inclusiveMin) {
      return `${question.label} must be at least ${inclusiveMin}`
    }

    if (typeof rules.lt === 'number' && numericValue >= rules.lt) {
      return `${question.label} must be less than ${rules.lt}`
    }

    const inclusiveMax = rules.lte ?? rules.max ?? rules.maximum
    if (typeof inclusiveMax === 'number' && numericValue > inclusiveMax) {
      return `${question.label} must be ${inclusiveMax} or less`
    }
  }

  const pattern = rules.pattern ?? rules.regex
  if (typeof pattern === 'string' && pattern !== '') {
    let regex: RegExp
    try {
      regex = new RegExp(pattern)
    } catch {
      return `${question.label} has an invalid validation pattern`
    }

    if (!regex.test(valueText)) {
      return `${question.label} is invalid`
    }
  }

  return null
}

export function validateQuestionnaireAnswers(
  questionnaire: QuestionnaireDTOType | null | undefined,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): QuestionnaireValidationState {
  if (!questionnaire) return EMPTY_QUESTIONNAIRE_VALIDATION

  const errors: Record<string, string> = {}
  const visibleQuestionIds: string[] = []
  const requiredQuestionIds: string[] = []

  questionnaire.pages.forEach((page) => {
    if (!evaluateQuestionnaireConditions(page.displayIf, answers, context)) return

    page.questions.forEach((question) => {
      if (!isQuestionVisible(question, answers, context)) return

      visibleQuestionIds.push(question.questionId)
      if (question.type === 'content') return

      const value = answers[question.questionId]
      const required = isQuestionRequired(question, answers, context)
      if (required) {
        requiredQuestionIds.push(question.questionId)
      }

      if (required && isAnswerEmpty(value)) {
        errors[question.questionId] = `${question.label} is required`
        return
      }

      if (value !== undefined && !isAnswerEmpty(value)) {
        const ruleError = validateRules(question, value)
        if (ruleError) {
          errors[question.questionId] = ruleError
        }
      }
    })
  })

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    visibleQuestionIds,
    requiredQuestionIds,
  }
}

export function getVisibleQuestionnaireAnswers(
  questionnaire: QuestionnaireDTOType | null | undefined,
  answers: QuestionnaireAnswers,
  context: QuestionnaireContext = {},
): QuestionnaireAnswers {
  const visibleQuestionIds = new Set(getVisibleQuestionIds(questionnaire, answers, context))
  return Object.entries(answers).reduce<QuestionnaireAnswers>(
    (visibleAnswers, [questionId, value]) => {
      if (!visibleQuestionIds.has(questionId) || isAnswerEmpty(value)) return visibleAnswers
      visibleAnswers[questionId] = value
      return visibleAnswers
    },
    {},
  )
}
